import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import {
  getNlsDictionary,
  getNlsTypography,
  interpolateNlsString,
  normalizeNlsLocale,
} from './nlsRuntime'
import type { NlsInterpolationValues, NlsLocale, NlsTypographyPolicy } from './nlsTypes'
import type { NlsKey, NlsStringMap } from './strings_nls'

export const NLS_LOCALE_STORAGE_KEY = 'timelining.nls.locale'

export type NlsContextValue = {
  locale: NlsLocale
  setLocale: (locale: NlsLocale | string) => void
  dictionary: NlsStringMap
  typography: NlsTypographyPolicy
  dir: NlsTypographyPolicy['dir']
  t: (key: NlsKey, values?: NlsInterpolationValues) => string
}

const getBrowserNlsLocale = (): NlsLocale => {
  if (typeof window === 'undefined') return 'en'
  return normalizeNlsLocale(window.navigator?.language ?? window.navigator?.languages?.[0])
}

export const getStoredNlsLocale = (): NlsLocale => {
  if (typeof window === 'undefined') return 'en'
  try {
    return normalizeNlsLocale(window.localStorage.getItem(NLS_LOCALE_STORAGE_KEY) ?? getBrowserNlsLocale())
  } catch {
    return getBrowserNlsLocale()
  }
}

export const setStoredNlsLocale = (locale: NlsLocale): void => {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(NLS_LOCALE_STORAGE_KEY, locale)
  } catch {
    // Storage can be blocked in private or embedded contexts. Runtime state still works.
  }
}

const applyLocaleToDocument = (locale: NlsLocale, typography: NlsTypographyPolicy): void => {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  root.lang = locale
  root.dir = typography.dir
  root.dataset.nlsLocale = locale
  root.style.setProperty('--nls-font-scale', String(typography.fontScale))
  root.style.setProperty('--nls-compact-font-scale', String(typography.compactFontScale))
  root.style.setProperty('--nls-line-height', String(typography.lineHeight))
  root.style.setProperty('--nls-compact-line-height', String(typography.compactLineHeight))
}

// Runs synchronously before React mounts so the very first paint uses the
// correct dir/lang/typography. Idempotent with the NlsProvider effect below.
export const applyStoredNlsLocaleToDocument = (): void => {
  const locale = getStoredNlsLocale()
  applyLocaleToDocument(locale, getNlsTypography(locale))
}

const NlsContext = createContext<NlsContextValue | null>(null)

export const NlsProvider = ({
  children,
  forcedLocale,
  initialLocale,
}: {
  children: ReactNode
  forcedLocale?: NlsLocale
  initialLocale?: NlsLocale
}) => {
  const [locale, setLocaleState] = useState<NlsLocale>(() => forcedLocale ?? initialLocale ?? getStoredNlsLocale())

  const setLocale = useCallback((nextLocale: NlsLocale | string) => {
    if (forcedLocale) return
    setLocaleState(normalizeNlsLocale(nextLocale))
  }, [forcedLocale])

  const effectiveLocale = forcedLocale ?? locale
  const dictionary = useMemo(() => getNlsDictionary(effectiveLocale), [effectiveLocale])
  const typography = useMemo(() => getNlsTypography(effectiveLocale), [effectiveLocale])

  useEffect(() => {
    if (forcedLocale) return
    setStoredNlsLocale(effectiveLocale)
    applyLocaleToDocument(effectiveLocale, typography)
  }, [effectiveLocale, typography, forcedLocale])

  const t = useCallback(
    (key: NlsKey, values?: NlsInterpolationValues) => interpolateNlsString(dictionary[key], values),
    [dictionary],
  )

  const value = useMemo<NlsContextValue>(() => ({
    locale: effectiveLocale,
    setLocale,
    dictionary,
    typography,
    dir: typography.dir,
    t,
  }), [dictionary, effectiveLocale, setLocale, t, typography])

  return <NlsContext.Provider value={value}>{children}</NlsContext.Provider>
}

// Fallback t() used when useNls is called outside <NlsProvider>. Preserves
// the historical behavior of returning English so tests that render leaf
// components without the provider keep working; the dev warning surfaces
// missing providers without breaking anything.
let orphanUseNlsWarned = false
const warnOrphanUseNlsOnce = () => {
  if (orphanUseNlsWarned) return
  orphanUseNlsWarned = true
  if (typeof console === 'undefined') return
  const isProd = typeof import.meta !== 'undefined' && (import.meta as { env?: { PROD?: boolean } }).env?.PROD
  if (isProd) return
  console.warn('[nls] useNls() was called outside <NlsProvider>. Falling back to English. Wrap the tree in NlsProvider to get real locale switching.')
}

let orphanFallbackValue: NlsContextValue | null = null
const getOrphanFallbackValue = (): NlsContextValue => {
  if (orphanFallbackValue) return orphanFallbackValue
  const locale = normalizeNlsLocale('en')
  const typography = getNlsTypography(locale)
  const dictionary = getNlsDictionary(locale)
  orphanFallbackValue = {
    locale,
    setLocale: () => {},
    dictionary,
    typography,
    dir: typography.dir,
    t: (key, values) => interpolateNlsString(dictionary[key], values),
  }
  return orphanFallbackValue
}

export const useNls = (): NlsContextValue => {
  const value = useContext(NlsContext)
  if (value) return value
  warnOrphanUseNlsOnce()
  return getOrphanFallbackValue()
}
